import {
    ChangeDetectionStrategy,
    Component,
    computed,
    OnInit,
    output,
    Signal,
    signal,
    ViewChild,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { SpreadSheetService } from "../../s25-spreadsheet/spreadsheet.service";
import { S25Util } from "../../../util/s25-util";
import { Document, DocumentListItem, DocumentService } from "../../../services/document.service";
import { Table } from "../../s25-table/Table";
import { Bind } from "../../../decorators/bind.decorator";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25GenericDropdownComponent } from "../../s25-dropdown/generic/s25.generic.dropdown.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { Doc } from "./s25.document.const";
import { DocumentUtil } from "./s25.document.util";
import { S25EditableTextComponent } from "../../s25-editable/s25-editable-text/s25.editable.text.component";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { VariableI } from "../../../pojo/VariableI";
import { ReportBuilderUtil } from "./s25.report.builder.util";
import { PartialBy } from "../../../pojo/Util";

@Component({
    standalone: true,
    selector: "s25-ng-import-document",
    template: `
        <label for="import-document-file">Select an exported document</label>
        <input id="import-document-file" type="file" accept=".xlsx" (change)="onFileChange($event)" />

        @if (document()) {
            <div class="document">
                <label for="document-name">Name</label>
                <input
                    [(ngModel)]="document()!.document_name"
                    id="document-name"
                    name="document-name"
                    class="c-input"
                    type="text"
                    maxlength="64"
                />

                <label>Scope</label>
                <p>{{ Doc.type[document()!.document_type].label }}</p>

                @if (document()!.file_name) {
                    <label for="output-filename">Output Filename </label>
                    <input
                        id="output-filename"
                        name="output-filename"
                        class="c-input"
                        [placeholder]=""
                        [(ngModel)]="document()!.file_name"
                    />
                }

                <label for="output-filename" class="ngBold description">Description </label>
                <s25-ng-rich-text-editor [(modelValue)]="document()!.description" />
            </div>
            @if (subreports()?.length) {
                <div class="subreports">
                    <label>Subreports</label>
                    <s25-ng-table [dataSource]="tableConfig" />
                </div>
            }
            <s25-ng-button class="import" [type]="'primary'" [onClick]="import">Import</s25-ng-button>
        }
    `,
    styles: `
        label {
            font-weight: bold;
            padding-top: 1em;
            display: block;
        }
        .subreports {
            padding-bottom: 10rem;
        }

        .import {
            padding-top: 1em;
        }

        :host s25-ng-table,
        :host s25-ng-table ::ng-deep .tableWrapper {
            overflow: visible;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25TableModule, S25ButtonComponent, ReactiveFormsModule, FormsModule, S25RichTextEditorModule],
})
export class S25ImportDocumentComponent implements OnInit {
    readonly imported = output();

    readonly document = signal<Document | undefined>(undefined);
    readonly subreports = signal<MatchedDocument[] | undefined>(undefined);
    readonly documentList = signal<DocumentListItem[] | undefined>(undefined);
    readonly subreportList = signal<DocumentListItem[] | undefined>(undefined);
    readonly allDocuments: Signal<DocumentListItem[]> = computed(() =>
        (this.documentList() ?? []).concat(this.subreportList() ?? []),
    );
    tableConfig: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getTableRows,
        columns: [
            { id: "id", header: "Id" },
            { id: "name", header: "Name" },
            { id: "idMatch", header: "ID Match (name)" },
            { id: "nameMatch", header: "Name Match (id)" },
            { id: "mapTo", header: "Map To" },
        ],
    };
    protected readonly Doc = Doc;

    @ViewChild(S25TableComponent) table: S25TableComponent;

    ngOnInit() {
        this.getDocumentList();
    }

    async getDocumentList() {
        if (!!this.allDocuments().length) return this.allDocuments();

        const [documents, subreports] = await Promise.all([
            DocumentService.getDocumentList(),
            DocumentService.getDocumentList({ scopes: ["subreport"] }),
        ]);
        this.documentList.set(documents);
        this.subreportList.set(subreports);
        return this.allDocuments();
    }

    async onFileChange(event: Event) {
        // Read file
        const [sheets, err] = await S25Util.Maybe(
            SpreadSheetService.readExcelFile<Document[][]>((event.target as any).files[0]),
        );
        const documents = sheets?.flat();
        if (err || !documents?.length) {
            alert("Could not read any document from file");
            return;
        }

        this.document.set(documents[0]);
        this.subreports.set(documents.slice(1));

        // Match subreports to existing documents
        const documentList = await this.getDocumentList();
        const byId = S25Util.array.mapBy(documentList, (doc) => doc.itemId);
        const byName = S25Util.array.mapBy(documentList, (doc) => doc.itemName);
        for (const doc of this.subreports()!) {
            const idMatch = byId.get(doc.document_id);
            const nameMatch = byName.get(doc.document_name);
            const fullMatch = idMatch === nameMatch ? idMatch : undefined;
            doc.matches = { id: idMatch, name: nameMatch, both: fullMatch };
            doc.importAs = "new";
        }

        await this.table?.refresh();
    }

    @Bind
    async getTableRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        const subreportList = this.subreportList() ?? [];
        const subreports: DropDownItem[] = BpeVarsConst.group(
            "Subreports",
            subreportList.map((doc) => <VariableI & { val: number }>{ val: doc.itemId, txt: doc.itemName }),
        );
        return {
            rows: (this.subreports() ?? []).map((doc) => {
                const mapTo: DropDownItem<"new" | number>[] = [{ itemId: "new", itemName: "New Subreport" }];
                if (doc.matches?.both) mapTo.push({ itemId: doc.matches.both.itemId, itemName: "Match (ID & Name)" });
                else {
                    if (doc.matches?.id) mapTo.push({ itemId: doc.matches.id.itemId, itemName: "ID Match" });
                    if (doc.matches?.name) mapTo.push({ itemId: doc.matches.name.itemId, itemName: "Name Match" });
                }
                mapTo.push(...(<DropDownItem<number>[]>subreports));

                return {
                    id: doc.document_id,
                    name: doc.document_name,
                    cells: {
                        id: { text: doc.document_id },
                        name: {
                            component: S25EditableTextComponent,
                            inputs: {
                                value: doc.document_name,
                            },
                            outputs: {
                                valueChange: (val, context) => {
                                    doc.document_name = val!;
                                },
                            },
                        } satisfies Table.Cell<S25EditableTextComponent>,

                        idMatch: { text: doc.matches?.id?.itemName },
                        nameMatch: { text: doc.matches?.name?.itemId },
                        mapTo: {
                            component: S25GenericDropdownComponent,
                            inputs: {
                                items: mapTo,
                                value: { itemId: "new" },
                            },
                            outputs: {
                                valueChange: (chosen, context) => {
                                    doc.importAs = chosen?.itemId as MatchedDocument["importAs"];
                                },
                            },
                        } satisfies Table.Cell<S25GenericDropdownComponent<"new" | number>>,
                    },
                };
            }),
        };
    }

    @Bind
    async import() {
        const document = this.document();
        if (!document) return;
        // Check that names are available
        const documentList = await this.getDocumentList();
        const taken = new Set(documentList.map((doc) => doc.itemName));
        const subreports = this.subreports() ?? [];
        for (const doc of [document, ...subreports.filter((doc) => doc.importAs === "new")]) {
            if (taken.has(doc.document_name)) {
                alert(`The name "${doc.document_name}" has already been taken. Please select a different name`);
                return;
            }
        }

        const subreportMap = S25Util.array.mapBy(subreports, (doc) => doc.document_id);
        await this.importDocument(document, subreportMap);

        this.imported.emit();
    }

    async importDocument(
        document: PartialBy<Document, "document_id">,
        subreports: Map<number, MatchedDocument>,
    ): Promise<number | undefined> {
        // Import subreports
        if (DocumentUtil.canReferenceDocuments(document)) {
            const subreportIds = ReportBuilderUtil.getSubreportIds(
                document.body_text,
                document.document_type as Doc.ReportBuilderTypeId,
            );

            const subreportMap: Record<number, number> = {};
            for (const id of subreportIds) {
                const subreport = subreports.get(id);
                if (!subreport) throw new Error(`Subreport ${id} not found`);
                if (subreport.importAs === "new") {
                    subreport.importAs = await this.importDocument(subreport, subreports);
                }
                subreportMap[id] = subreport.importAs!;
            }

            ReportBuilderUtil.mapSubreports(document, subreportMap);

            document.subreports = ReportBuilderUtil.getSubreportIds(
                document.body_text,
                Doc.type[document.document_type].id as Doc.ReportBuilderTypeId,
            );
        }

        // Import document
        document.document_id = undefined;
        return await DocumentService.saveDocument(document);
    }
}

type MatchedDocument = Document & {
    matches?: { id?: DocumentListItem; name?: DocumentListItem; both?: DocumentListItem };
    importAs?: "new" | number;
};
